import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Modal,
  Snackbar,
  Stack,
  Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  CurrentDebitInterface,
  PendingSubscriptionInterface
} from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { PaymentModal } from "../PaymentModal";
import { usePendingPayment } from "./PendingPaymentContext";

// Aviso do admin: uma vez por sessão de login, separado da abertura do modal.
const ADMIN_ALERT_FLAG = "pendingPaymentAdminAlert";

const formatCurrency = (value: number) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// A rota devolve o vencimento como YYYY-MM-DD (campo DATE, sem hora). Montamos
// a data pelos componentes para não deixar o navegador interpretar como UTC e
// exibir o dia anterior em Manaus (GMT-4).
const formatDate = (dateOnly: string) => {
  const [year, month, day] = (dateOnly ?? "").split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
};

// Converte a pendência (payload enxuto) para o shape que o PaymentModal espera.
const toDebit = (p: PendingSubscriptionInterface): CurrentDebitInterface => ({
  subscription_id: p.subscription_id,
  company_id: p.company_id,
  config_id: p.config_id,
  charged_amount: Number(p.charged_amount),
  paid_amount: Number(p.paid_amount ?? 0),
  is_in_debt: p.is_in_debt ?? 0,
  due_date: p.due_date,
  payment_date: null,
  created_at: p.created_at,
  updated_at: p.updated_at
});

const PendingPaymentPopup: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  // A busca e o estado do modal vivem no contexto, para a barra superior
  // poder mostrar o aviso e reabrir este modal a qualquer momento.
  const {
    pendings,
    total,
    companyCount: pendingCompanyCount,
    modalOpen: open,
    closeModal
  } = usePendingPayment();

  const [adminAlertOpen, setAdminAlertOpen] = useState(false);
  const adminAlertShown = useRef(false);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selected, setSelected] = useState<CurrentDebitInterface[]>([]);

  // Admin continua recebendo o aviso flutuante uma vez por sessão de login.
  useEffect(() => {
    if (userProfile !== "ADMIN" || pendings.length === 0) return;
    if (adminAlertShown.current) return;
    if (sessionStorage.getItem(ADMIN_ALERT_FLAG)) return;

    adminAlertShown.current = true;
    sessionStorage.setItem(ADMIN_ALERT_FLAG, "1");
    setAdminAlertOpen(true);
  }, [userProfile, pendings.length]);

  const handlePay = (p: PendingSubscriptionInterface) => {
    setSelected([toDebit(p)]);
    setPaymentOpen(true);
  };

  const openAdminSubscriptions = () => {
    setAdminAlertOpen(false);
    navigate("/subscriptions");
  };

  return (
    <>
      <Snackbar
        open={adminAlertOpen}
        onClose={() => setAdminAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 7 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setAdminAlertOpen(false)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={openAdminSubscriptions}
            >
              Ver mensalidades
            </Button>
          }
          sx={{ width: "100%", alignItems: "center" }}
        >
          {pendingCompanyCount === 1
            ? "Há 1 empresa com mensalidade pendente."
            : `Há ${pendingCompanyCount} empresas com mensalidades pendentes.`}
        </Alert>
      </Snackbar>

      <Modal open={open} onClose={() => closeModal()}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            color: "text.primary",
            borderRadius: "14px",
            boxShadow: 24,
            width: { xs: "92vw", sm: 460 },
            maxWidth: 460,
            maxHeight: "90vh",
            overflowY: "auto",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho com destaque de alerta */}
          <Box
            sx={{
              p: 2.5,
              background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FiAlertTriangle size={26} />
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                  Mensalidade em aberto
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {pendings.length === 1
                    ? "Você tem 1 cobrança pendente"
                    : `Você tem ${pendings.length} cobranças pendentes`}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => closeModal()}
              size="small"
              sx={{ color: "#fff" }}
            >
              <FiX />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              {pendings.map((p) => (
                <Box
                  key={p.subscription_id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "10px",
                    p: 1.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {p.company.company_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Venc.: {formatDate(p.due_date)}
                      {p.days_overdue != null && p.days_overdue > 0 && (
                        <Chip
                          label={`${p.days_overdue} dia(s) em atraso`}
                          color="error"
                          size="small"
                          sx={{ ml: 1, height: 20 }}
                        />
                      )}
                    </Typography>
                    <Typography fontWeight={800} sx={{ mt: 0.5 }}>
                      {formatCurrency(p.charged_amount)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => handlePay(p)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Pagar agora
                  </Button>
                </Box>
              ))}
            </Stack>

            {pendings.length > 1 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">Total em aberto</Typography>
                  <Typography fontWeight={800}>
                    {formatCurrency(total)}
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ mt: 2.5, textAlign: "right" }}>
              <Button onClick={() => closeModal()}>Pagar depois</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <PaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        debits={selected}
      />
    </>
  );
};

export default PendingPaymentPopup;
